import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    catchError,
    finalize,
    map,
    Observable,
    of,
    switchMap,
    throwError,
} from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from '../../../environments/environment';
import { apiConfig } from '../../api-url.config';
import { storeRedirectConfig } from './new-dashboard';
@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    private permissions: any[] = JSON.parse(
        localStorage.getItem('permissions') || '[]'
    );
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(username: any, password: any): Observable<any> {
        return this._httpClient
            .post(
                `${environment.API_END_POINT}${apiConfig.auth.resetPassword}`,
                {
                    username: username,
                    password: password
                }
            )
            .pipe(
                switchMap((response: any) => {
                    const user = response.responseData[0].records[0];
                    return of(user);
                })
            );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient
            .post(
                `${environment.API_END_POINT}${apiConfig.auth.login}`,
                credentials
            )
            .pipe(
                switchMap((response: any) => {
                    const records = response.responseData[0].records;
                    // Backend asks us to send this user to the new dashboard
                    if (records?.redirect) {
                        const config = { redirect: true, redirect_url: records.redirect_url };
                        storeRedirectConfig(config);
                        return of(config);
                    }
                    const user = records[0];
                    let countDown = 2700; // 45 minutes in seconds
                    localStorage.setItem("countDown", countDown.toString());
                    // Store the access token in the local storage

                    localStorage.setItem('loggedInUser', JSON.stringify(user));
                    if (user.multi_auth === 1 && user.email.includes("@") && user.email.includes(".com")) {
                        //nothing to do here
                    }
                    else {
                        this.accessToken = user.access_token;
                        // Set the authenticated flag to true
                        this._authenticated = true;

                        // Store the user on the user service
                        this._userService.user = user;
                    }

                    // Return a new observable with the response
                    return of(user);
                })
            );
    }
    loginWithGoogle(payload): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient
            .post(
                `${environment.API_END_POINT}${apiConfig.auth.googleSignIn}`,
                payload
            )
            .pipe(
                switchMap((response: any) => {
                    const records = response.responseData[0].records;
                    if (records?.redirect) {
                        const config = { redirect: true, redirect_url: records.redirect_url };
                        storeRedirectConfig(config);
                        return of(config);
                    }
                    const user = records[0];
                    // Store the access token in the local storage
                    this.accessToken = user.access_token;
                    localStorage.setItem('loggedInUser', JSON.stringify(user));
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = user;

                    // Return a new observable with the response
                    return of(user);
                })
            );
    }
    getOrganizationPreferences(org_id: any) {
        return this._httpClient
            .get(
                `${environment.API_END_POINT}${apiConfig.user.userOrganisation}/${org_id}${apiConfig.user.preferences}`
            )
            .pipe(
                map((res: any) => res),
                catchError((err: any) => {
                    return throwError(err.error);
                })
            );
    }

    getUserPermissions(keyName?: string) {
        this.getUserPermissionsFromApi().subscribe(
            (res: any) => {
                localStorage.setItem(
                    'permissions',
                    JSON.stringify(res.permissions)
                );
                this.permissions = res.permissions;
                if (keyName) {
                    this.isEnabled(keyName);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }

    public isEnabled(keyName: string) {
        if (!keyName) {
            return true;
        }
        if (!this.permissions.length) {
            // this.getUserPermissions(keyName);
            return;
        }

        return this.permissions.filter(
            (permission) =>
                permission.permissions.keyname.toLowerCase() ==
                keyName.toLowerCase()
        ).length
            ? true
            : false;
    }

    getUserPermissionsFromApi() {
        return this._httpClient
            .get(
                `${environment.API_END_POINT}${apiConfig.user.userPermissions}`
            )
            .pipe(
                map((res: any) => res),
                catchError((err: any) => {
                    return throwError(err.error);
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient
            .post('api/auth/refresh-access-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    public signOut(): Observable<any> {
        this._authenticated = false;
        return this._httpClient
            .post(`${environment.API_END_POINT}${apiConfig.auth.logout}`, {})
            .pipe(
                finalize(() => {
                    localStorage.clear();
                    sessionStorage.clear();
                })
            )
            .pipe(
                map(() => { }),
                catchError((err: any) => {
                    return throwError(err.error);
                })
            );

        // // Return the observable
        // return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        // if (this._authenticated) {
        //     return of(true);
        // }
        if (this.accessToken) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    public loginIMS(email: string, type: string = "string") {
        const body = {
            email: email,
            type: type
        };
        return this._httpClient
            .post(
                `${environment.IMS_API_END_POINT}${apiConfig.auth.IMSLogin}`,
                body
            )
            .pipe();
    }

    VerifyOTP(otp: any, key: any, email: string) {
        const body = {
            otp: otp,
            detailskey: key,
            email: email
        };
        return this._httpClient
            .post(
                `${environment.IMS_API_END_POINT}${apiConfig.auth.VerifyOTP}`,
                body
            )
            .pipe(
                switchMap((response: any) => {
                    const user: any = JSON.parse(localStorage.getItem("loggedInUser"));
                    let countDown = 2700; // 45 minutes in seconds
                    localStorage.setItem("countDown", countDown.toString());
                    // Store the access token in the local storage
                    this.accessToken = user.access_token;
                    // Set the authenticated flag to true
                    this._authenticated = true;
                    // Store the user on the user service
                    this._userService.user = user;
                    // Return a new observable with the response
                    return of(user);
                })
            );

    }
}
